/**
 * Vocabulário padrão da Berytus para forma farmacêutica e classificação do medicamento. Os canais pedem valores de uma
 * lista própria (a Shopee: "Comprimidos", "Cápsulas", "Xaropes"...); a Berytus guarda o padrão e traduz para cada canal.
 *
 * normalizePharmaForm entende a apresentação da CMED/ABCFarma ("500MG COM REV CT BL AL PLAS PVC X 20", "xpe fr 100ml",
 * "CREM DERM CT BG AL X 40 G") e textos livres ("comprimido revestido", "Cápsula").
 */
export const PHARMA_FORMS = [
  'Comprimido', 'Comprimido revestido', 'Comprimido efervescente', 'Comprimido mastigável', 'Comprimido orodispersível', 'Comprimido de liberação prolongada',
  'Cápsula', 'Cápsula gelatinosa mole', 'Drágea', 'Pastilha', 'Goma de mascar',
  'Xarope', 'Solução oral', 'Gotas', 'Suspensão oral', 'Elixir', 'Emulsão',
  'Pó para solução oral', 'Pó para suspensão oral', 'Granulado', 'Sachê',
  'Creme', 'Pomada', 'Gel', 'Loção', 'Solução tópica', 'Spray', 'Aerossol', 'Espuma', 'Adesivo transdérmico', 'Emplastro', 'Shampoo', 'Sabonete',
  'Colírio', 'Solução nasal', 'Solução otológica', 'Pomada oftálmica',
  'Supositório', 'Óvulo', 'Creme vaginal', 'Gel vaginal', 'Enema',
  'Solução injetável', 'Pó para solução injetável', 'Suspensão injetável', 'Ampola',
  'Pó para inalação', 'Solução para inalação', 'Spray oral',
] as const;
export type PharmaForm = (typeof PHARMA_FORMS)[number];

const strip = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Ordem importa: o mais específico primeiro. Siglas da CMED/ABCFarma e nomes por extenso.
const RULES: [RegExp, PharmaForm][] = [
  [/\b(com|comp|cpr)\.? (efer|efv)|\befervescente/, 'Comprimido efervescente'],
  [/\b(com|comp|cpr)\.? mast|\bmastigave/, 'Comprimido mastigável'],
  [/\b(com|comp|cpr)\.? (orodisp|disp)|\borodispersive/, 'Comprimido orodispersível'],
  [/\b(com|comp|cpr)\.? (rev\.? )?(lib|ret|lp|prol)|\bliberacao (prolongada|controlada|retardada)/, 'Comprimido de liberação prolongada'],
  [/\b(com|comp|cpr)\.? rev|\bcomprimidos? revestid|\bcpr rev/, 'Comprimido revestido'],
  [/\bcap(s|sula)?\.? (gel|mole)|\bcapsulas? gelatinosas? mole|\bsoftgel/, 'Cápsula gelatinosa mole'],
  [/\bdrg\b|\bdrage/, 'Drágea'],
  [/\b(com|comp|cpr|comps)\b|\bcomprimido/, 'Comprimido'],
  [/\b(cap|caps|cps)\b|\bcapsula/, 'Cápsula'],
  [/\bpast\b|\bpastilha/, 'Pastilha'],
  [/\bgoma\b|\bchiclete/, 'Goma de mascar'],
  [/\b(xpe|xar)\b|\bxarope/, 'Xarope'],
  [/\b(gts|gt|gotas)\b|\bsol(ucao)?\.? (or(al)?\.? )?(gt|got)/, 'Gotas'],
  [/\bsus(p|pensao)?\.? (or|oral)\b|\bsusp\b(?!.*inj)/, 'Suspensão oral'],
  [/\belix\b|\belixir/, 'Elixir'],
  [/\bemu(l|lsao)\b/, 'Emulsão'],
  [/\bpo (p\/ ?|para )?(sol|susp)\.? inj|\bpo liof|\bliofilizado/, 'Pó para solução injetável'],
  [/\bsus(p)?\.? inj/, 'Suspensão injetável'],
  [/\bsol(ucao)?\.? inj|\binjetavel|\binj\b/, 'Solução injetável'],
  [/\bamp\b|\bampola/, 'Ampola'],
  [/\bpo (p\/ ?|para )?(sol|susp)?\.? ?(inal)|\bpo inal/, 'Pó para inalação'],
  [/\bsol(ucao)?\.? (p\/ ?|para )?inal|\bnebuliza/, 'Solução para inalação'],
  [/\bpo (p\/ ?|para )?sus/, 'Pó para suspensão oral'],
  [/\bpo (p\/ ?|para )?sol/, 'Pó para solução oral'],
  [/\bgran(ulado)?\b/, 'Granulado'],
  [/\b(sache|env)\b|\bsaches?\b/, 'Sachê'],
  [/\bcol(irio)?\b|\bsol(ucao)?\.? oft/, 'Colírio'],
  [/\bpom\.? oft|\bpomada oftalm/, 'Pomada oftálmica'],
  [/\bsol(ucao)?\.? (nas|nasal)|\bspray nasal|\bdescongestionante nasal/, 'Solução nasal'],
  [/\bsol(ucao)?\.? (oto|otol)/, 'Solução otológica'],
  [/\bcrem(e)?\.? vag|\bcreme vaginal/, 'Creme vaginal'],
  [/\bgel vag/, 'Gel vaginal'],
  [/\bovulo|\bovl\b/, 'Óvulo'],
  [/\bsup(ositorio)?\b/, 'Supositório'],
  [/\benema\b/, 'Enema'],
  [/\b(crem|creme)\b/, 'Creme'],
  [/\b(pom|pomada)\b/, 'Pomada'],
  [/\bgel\b/, 'Gel'],
  [/\b(loc|locao)\b/, 'Loção'],
  [/\badesivo|\btransderm/, 'Adesivo transdérmico'],
  [/\bemplastro/, 'Emplastro'],
  [/\b(aer|aerossol|aerosol)\b/, 'Aerossol'],
  [/\bspray (oral|bucal)/, 'Spray oral'],
  [/\bspray\b/, 'Spray'],
  [/\bespuma\b/, 'Espuma'],
  [/\b(xampu|shampoo)\b/, 'Shampoo'],
  [/\bsabonete\b/, 'Sabonete'],
  [/\bsol(ucao)?\.? (top|derm|cutanea|capilar)/, 'Solução tópica'],
  [/\bsol(ucao)?\.? or(al)?\b|\bsol oral\b/, 'Solução oral'],
];

/** Forma farmacêutica padrão a partir da apresentação ou do texto livre (null = não reconhecida). */
export function normalizePharmaForm(text: string | null | undefined): PharmaForm | null {
  if (!text) return null;
  const s = ` ${strip(text).replace(/[,;()]/g, ' ').replace(/\s+/g, ' ').trim()} `;
  const exact = PHARMA_FORMS.find((f) => strip(f) === s.trim());
  if (exact) return exact;
  for (const [re, form] of RULES) if (re.test(s)) return form;
  return null;
}

/** Blister/cartela pela apresentação da CMED ("CT BL AL PLAS PVC X 20") ou texto ("blister"). */
export function detectBlister(text: string | null | undefined): boolean | null {
  if (!text) return null;
  const s = ` ${strip(text)} `;
  if (/\bbl\b|\bblister|\bblist\b|\bstrip\b|\bcartela/.test(s)) return true;
  if (/\b(fr|frasco|bg|bisnaga|fa|amp|ampola|env|sache|pote|lata|tubo)\b/.test(s)) return false;
  return null;
}

/** Unidades na embalagem pela apresentação: "... X 20" (CMED), "cx 30 comp" (ABCFarma). */
export function packUnitsFrom(text: string | null | undefined): number | null {
  if (!text) return null;
  const s = strip(text);
  const cx = /\bcx\.? (\d{1,4}) (com|comp|cap|caps|drg|past|env|sache|amp|sup|ovl|ades|tab)/.exec(s);
  if (cx) return Number(cx[1]);
  const xs = [...s.matchAll(/\bx\s?(\d{1,4})\b(?!\s?(ml|l|g|mg|mcg|kg|cm|un\/))/g)];
  return xs.length ? Number(xs[xs.length - 1]![1]) : null;
}

/** Classificação do medicamento (categoria regulatória). */
export const REGULATORY_LABEL = {
  novo: 'Medicamento novo', referencia: 'Medicamento de referência', generico: 'Medicamento genérico', similar: 'Medicamento similar',
  especifico: 'Medicamento específico', fitoterapico: 'Fitoterápico', biologico: 'Biológico', dinamizado: 'Dinamizado',
  notificado: 'Baixo risco – notificado', outro: 'Outro',
} as const;
export type RegulatoryCategory = keyof typeof REGULATORY_LABEL;

/** "Genérico", "GENERICO", "Medicamento Similar", "Novo", "Biológico", "Fitoterápico"... da CMED e da ABCFarma. */
export function normalizeRegulatory(text: string | null | undefined): RegulatoryCategory | null {
  if (!text) return null;
  const s = strip(text);
  if (/generic/.test(s)) return 'generico';
  if (/similar/.test(s)) return 'similar';
  if (/referencia/.test(s)) return 'referencia';
  if (/\bnovo\b/.test(s)) return 'novo';
  if (/biolog/.test(s)) return 'biologico';
  if (/fitoter/.test(s)) return 'fitoterapico';
  if (/especifico/.test(s)) return 'especifico';
  if (/dinamiz|homeopat/.test(s)) return 'dinamizado';
  if (/notific|baixo risco/.test(s)) return 'notificado';
  return null;
}
